import React, { useRef, useState } from 'react';
import { FileText, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadAttachment } from '@/api/support';
import { friendlyError } from '@/lib/apiError';
import { MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES } from '@/lib/support';

const isImage = (a) => (a?.mime || '').startsWith('image/');

/**
 * Picks files, uploads each as soon as it is chosen, and hands the parent the
 * uploaded attachment list. Shows a preview tile per file with remove.
 */
export function AttachmentPicker({ value = [], onChange, disabled, compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const pick = async (files) => {
    const room = MAX_ATTACHMENTS - value.length;
    const chosen = Array.from(files || []).slice(0, Math.max(0, room));
    if (!chosen.length) {
      if (room <= 0) toast.error(`You can attach up to ${MAX_ATTACHMENTS} files`);
      return;
    }
    const tooBig = chosen.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name} is larger than 10 MB`);
      return;
    }
    setUploading((n) => n + chosen.length);
    const done = [];
    for (const file of chosen) {
      try {
        done.push(await uploadAttachment(file));
      } catch (err) {
        toast.error(friendlyError(err, `Couldn't upload ${file.name}`));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (done.length) onChange([...value, ...done]);
  };

  return (
    <div className="space-y-2">
      {(value.length > 0 || uploading > 0) && (
        <ul className="flex flex-wrap gap-2" aria-label="Attachments">
          {value.map((a, i) => (
            <li key={a.url} className="relative">
              {isImage(a) ? (
                <img src={a.url} alt={a.name} className="h-16 w-16 rounded-lg object-cover ring-1 ring-ink-200" />
              ) : (
                <div className="flex h-16 w-28 flex-col items-center justify-center gap-1 rounded-lg bg-surface-sunken px-2 ring-1 ring-ink-200">
                  <FileText className="h-5 w-5 text-ink-500" aria-hidden="true" />
                  <span className="w-full truncate text-center text-[11px] text-ink-700">{a.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                aria-label={`Remove ${a.name}`}
                className="lk-focus absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink-900 text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
          {uploading > 0 && (
            <li className="flex h-16 w-16 items-center justify-center rounded-lg bg-surface-sunken ring-1 ring-ink-200">
              <Loader2 className="h-5 w-5 animate-spin text-ink-500" aria-label="Uploading" />
            </li>
          )}
        </ul>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={disabled || uploading > 0 || value.length >= MAX_ATTACHMENTS}
        onClick={() => inputRef.current?.click()}
        className={
          compact
            ? 'lk-focus flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-500 hover:bg-surface-muted disabled:opacity-40'
            : 'lk-btn-ghost text-sm disabled:opacity-50'
        }
        aria-label="Attach photos or PDF"
      >
        <Paperclip className="h-4 w-4" />
        {!compact && <span>Attach photos or PDF ({value.length}/{MAX_ATTACHMENTS})</span>}
      </button>
    </div>
  );
}

/** Attachments shown inside a message bubble. */
export function AttachmentList({ items = [], onDark = false }) {
  if (!items?.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {items.map((a) => (
        <li key={a.url}>
          <a href={a.url} target="_blank" rel="noreferrer" className="lk-focus block rounded-lg" title={a.name}>
            {isImage(a) ? (
              <img src={a.url} alt={a.name || 'Attachment'} className="h-24 w-24 rounded-lg object-cover ring-1 ring-black/10" loading="lazy" />
            ) : (
              <span
                className={`inline-flex max-w-[12rem] items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ring-1 ${
                  onDark ? 'bg-white/15 text-white ring-white/20' : 'bg-white text-ink-700 ring-ink-200'
                }`}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{a.name || 'File'}</span>
              </span>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}
